import { useCustom } from '@refinedev/core';
import { Card, Col, Progress, Row, Statistic, Typography } from 'antd';
import { Area } from '@ant-design/charts';
import dayjs from 'dayjs';
import type { DailyAccrualStatsOut } from '@/api/generated/schemas';
import { formatLedger, formatPayable } from '@/utils/money';
import { cleanFilters, type SettlementStatsFilters } from './filters';
import { fillDailySeries, type DailyPoint } from './seriesFill';

const AREA_COLOR = '#1783FF';

type Props = { filters: SettlementStatsFilters };

export const SettlementAccrualCard = ({ filters }: Props) => {
  const { query } = useCustom<DailyAccrualStatsOut>({
    url: 'settlement/royalties_daily/stats/accrual_trend',
    method: 'get',
    config: { query: { days: 30, ...cleanFilters(filters) } },
    queryOptions: { refetchInterval: 60_000 },
  });

  const stats = query.data?.data;
  const daily = fillDailySeries(stats?.entries ?? [], 30);

  const dayOfMonth = dayjs().date();
  const daysInMonth = dayjs().daysInMonth();

  return (
    <Card title="Bieżący miesiąc — dzienny przebieg" loading={query.isLoading}>
      <Row gutter={[24, 16]}>
        <Col xs={24} md={8} lg={6}>
          <Statistic
            title="Miesiąc do dziś (faktura)"
            value={stats?.mtd_payable_amount ?? '0'}
            formatter={(v) => formatPayable(v as string | number)}
          />
          <Typography.Text
            type="secondary"
            style={{ display: 'block', marginTop: 4 }}
          >
            Księga: {formatLedger(stats?.mtd_amount ?? '0')}
          </Typography.Text>
          {/* A forecast has no invoice value — the days it projects have not
              been rounded into one. */}
          <Typography.Text
            type="secondary"
            style={{ display: 'block', marginTop: 4 }}
          >
            ≈ {formatLedger(stats?.projected_month_amount ?? '0')} na koniec
            miesiąca (księga)
          </Typography.Text>
          <Progress
            percent={Math.round((dayOfMonth / daysInMonth) * 100)}
            size="small"
            showInfo={false}
            strokeColor={AREA_COLOR}
            style={{ marginTop: 8, marginBottom: 0 }}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Dzień {dayOfMonth} z {daysInMonth} — projekcja liniowa, nie
            obietnica
          </Typography.Text>
        </Col>
        <Col xs={24} md={16} lg={18}>
          <Area
            data={daily}
            xField="day"
            yField="amount"
            height={180}
            style={{ fill: AREA_COLOR, fillOpacity: 0.2 }}
            line={{ style: { stroke: AREA_COLOR, lineWidth: 2 } }}
            label={{
              text: (d: DailyPoint) => (d.today ? 'dziś' : ''),
              position: 'top',
              style: { fill: '#8c8c8c', fontSize: 11, dy: -4 },
            }}
            axis={{
              x: { labelFormatter: (v: string) => v.slice(5) },
            }}
            tooltip={{
              items: [
                (d: DailyPoint) => ({
                  name: 'Naliczenia',
                  value: d.today
                    ? `${formatLedger(d.amount)} (dzień otwarty)`
                    : formatLedger(d.amount),
                  color: AREA_COLOR,
                }),
              ],
            }}
          />
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            Ostatnie 30 dni; dane spływają co ~5 min, dni bez emisji = 0
            (weekendowe doły są naturalne).
          </Typography.Text>
        </Col>
      </Row>
    </Card>
  );
};
